import { useCallback, useState } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useCanGoBack, useNavigate, useRouter } from '@tanstack/react-router';
import { meOptions } from '@/features/auth/api';
import {
  projectEpicsOptions,
  projectMembersOptions,
  projectTagsOptions,
} from '@/features/projects/api';
import { taskDetailOptions, taskStatusOptions, tasksListOptions } from '@/features/tasks/api';
import { useDeleteTask } from '@/features/tasks/hooks';
import { TaskFormBody } from '@/features/tasks/components/TaskFormBody';
import { useTaskForm } from '@/features/tasks/task-form-context';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/_authed/projects/$projectId/tasks_/$taskId')({
  // Same query set TaskFormBody reads via useSuspenseQuery, so the page paints
  // without waterfalls (mirrors tasks.tsx).
  loader: ({ context, params }) =>
    Promise.all([
      context.queryClient.ensureQueryData(meOptions()),
      context.queryClient.ensureQueryData(projectMembersOptions(params.projectId)),
      context.queryClient.ensureQueryData(projectTagsOptions(params.projectId)),
      context.queryClient.ensureQueryData(projectEpicsOptions(params.projectId)),
      context.queryClient.ensureQueryData(tasksListOptions(params.projectId, {})),
      context.queryClient.ensureQueryData(taskStatusOptions(params.projectId)),
      context.queryClient.ensureQueryData(taskDetailOptions(params.taskId)),
    ]),
  component: TaskFullPage,
});

function TaskFullPage() {
  const { projectId, taskId } = Route.useParams();
  const navigate = useNavigate();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const { openEdit } = useTaskForm();

  const { data: task } = useSuspenseQuery(taskDetailOptions(taskId));

  // Return to the previous page via browser history, falling back to the Tasks
  // tab on a deep link / hard reload (no history to pop).
  const back = useCallback(() => {
    if (canGoBack) {
      router.history.back();
    } else {
      void navigate({ to: '/projects/$projectId/tasks', params: { projectId } });
    }
  }, [canGoBack, router, navigate, projectId]);

  // Minimize — the mirror of TaskFormDialog's maximize. `openEdit` first, then
  // navigate: by the time the layout's Effect A writes `?task=<id>` on the origin
  // tab, the shared state is already `edit`, so Effect B's deep-link redirect bails
  // on its `state.kind !== 'closed'` guard instead of bouncing back to this page.
  // Reuses `back()` so a deep-linked visit (no history) falls back to the Tasks tab.
  const minimize = useCallback(() => {
    openEdit(task);
    back();
  }, [openEdit, task, back]);

  const [deleteTaskId, setDeleteTaskId] = useState<string | null>(null);
  const deleteMutation = useDeleteTask(() => {
    setDeleteTaskId(null);
    back();
  });

  return (
    <>
      <div className="relative flex h-full flex-col p-6">
        <div className="min-h-0 flex-1">
          <TaskFormBody
            projectId={projectId}
            variant="page"
            mode="edit"
            task={task}
            onClose={back}
            onDelete={() => setDeleteTaskId(task.id)}
            onToggleView={minimize}
          />
        </div>
      </div>

      <Dialog open={deleteTaskId !== null} onOpenChange={(open) => !open && setDeleteTaskId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete task?</DialogTitle>
            <DialogDescription>This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteTaskId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => deleteTaskId && deleteMutation.mutate(deleteTaskId)}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
